import { memo } from 'react'
import { CircleStop, Loader2, Play } from 'lucide-react'
import { Handle, Position, type NodeProps } from 'reactflow'
import { Checkbox } from '@/components/ui/checkbox'
import { IntuiAvatar } from '@/components/intueri/IntuiAvatar'
import { AGENT_ALIASES, AgentId, getAgentAvatarImage } from '@/constants/agentNames'
import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'
import { NODE_TONE_CLASSES, nodeStatusDisplay } from '../statusDisplay'
import type { VersionMarkData, WorkflowGraphNodeData } from '../workflowTypes'

/**
 * Engine statuses that mean a run is in flight (or just finished). The footer
 * earns its space only then — "Ready" means this step has not been reached yet,
 * which is a real distinction from Success / In Progress / Need Confirmation.
 *
 * Pre-run canvases are NOT in this set: Outline emits `draft`, and Detail emits
 * the node's kind (`agentic`, `verify`, `terminal`, …). Both fall through
 * `nodeStatusDisplay` to the Ready label, which is the same as saying nothing.
 */
const LIVE_RUN_STATUSES = new Set([
  'ready',
  'pending',
  'running',
  'completed',
  'ok',
  'success',
  'completed_with_unknowns',
  'waiting_for_user',
  'awaiting_approval',
  'skipped',
  'replaced',
  'failed',
  'blocked',
  'guardrail_stopped',
  'budget_limited',
  'error',
  'cancelled',
])

/**
 * Invisible edge anchors on all four sides (ids `s-*` / `t-*`), so the snake
 * layout can route sequential edges down, up, or across a column turn — see
 * snakeEdgeHandles in workflowGraph.ts.
 */
function EdgeHandles() {
  const hidden = '!size-2 !border-0 !bg-transparent'
  return (
    <>
      <Handle id="t-top" type="target" position={Position.Top} className={hidden} />
      <Handle id="t-bottom" type="target" position={Position.Bottom} className={hidden} />
      <Handle id="t-left" type="target" position={Position.Left} className={hidden} />
      <Handle id="t-right" type="target" position={Position.Right} className={hidden} />
      <Handle id="s-top" type="source" position={Position.Top} className={hidden} />
      <Handle id="s-bottom" type="source" position={Position.Bottom} className={hidden} />
      <Handle id="s-left" type="source" position={Position.Left} className={hidden} />
      <Handle id="s-right" type="source" position={Position.Right} className={hidden} />
    </>
  )
}

/** About two lines of the marker's cause line, so what the step found still shows on the third. */
const CAUSE_STEP_TITLE_MAX = 50

/** A step title cut at a word to at most `max` characters; whole when it already fits. */
function clipTitle(title: string, max: number): string {
  if (title.length <= max) return title
  const cut = title.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return `${space > 0 ? cut.slice(0, space) : cut}…`
}

/**
 * What made a later plan version, in the engineer's words: the step and what it found, a file, or an
 * edit. `clip` shortens the step's title (it can be a whole instruction) so what it found still shows.
 */
function versionCauseText(t: ReturnType<typeof useTranslation>['t'], mark: VersionMarkData, clip = false): string {
  const c = mark.cause
  if (c.kind === 'file') return t('workflow:builder.version_from_file', { defaultValue: 'From the file you sent' })
  if (c.kind === 'edit') return t('workflow:builder.version_edited', { defaultValue: 'You edited the plan' })
  if (c.kind === 'unknown') return t('workflow:builder.version_changed', { defaultValue: 'The plan changed' })
  const step = clip ? clipTitle(c.stepTitle, CAUSE_STEP_TITLE_MAX) : c.stepTitle
  const f = mark.finding
  if (f?.kind === 'coverage')
    return t('workflow:builder.version_found_coverage', { defaultValue: '{{step}} found only {{observed}} of {{expected}} samples', step, observed: f.observed, expected: f.expected })
  if (f?.kind === 'empty') return t('workflow:builder.version_found_empty', { defaultValue: '{{step}} found no data', step })
  if (f?.kind === 'failed') return t('workflow:builder.version_found_failed', { defaultValue: '{{step}} could not read its data', step })
  return t('workflow:builder.version_found', { defaultValue: '{{step}} found something that changes the plan', step })
}

/** A later plan version's marker: the branch it starts hangs from the step that made it. */
function VersionMarker({ mark }: { mark: VersionMarkData }) {
  const { t } = useTranslation()
  const cause = versionCauseText(t, mark)
  const shortCause = versionCauseText(t, mark, true)
  return (
    <div className="flex w-[272px] flex-col overflow-hidden rounded-lg border border-dashed border-cyan-400 bg-cyan-50/60 shadow-sm dark:border-cyan-700 dark:bg-cyan-950/30">
      <EdgeHandles />
      <div className="flex flex-col gap-1 px-3 py-2.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-cyan-700 dark:text-cyan-400">
          {t('workflow:builder.version_title', { defaultValue: 'Plan v{{version}}', version: mark.version })}
        </span>
        {/* Three lines at most, the whole sentence on hover: rows are 200px apart (workflowGraph.ts),
            and a taller marker runs over the step a wrapped branch puts below it (FS-720). */}
        <span title={cause} className="line-clamp-3 text-sm font-semibold leading-snug text-foreground">
          {shortCause}
        </span>
        {mark.cause.kind === 'step' && mark.cause.file && (
          <span className="text-xs text-muted-foreground">{t('workflow:builder.version_with_file', { defaultValue: 'The file you sent is included too.' })}</span>
        )}
        {mark.reason && <p className="line-clamp-3 text-xs leading-snug text-muted-foreground">{mark.reason}</p>}
      </div>
    </div>
  )
}

/** The agent persona avatar for a node: match by title, else the plant-support default. */
function nodeAvatar(title: string): { src?: string; name: string } {
  const matched = getAgentAvatarImage(title)
  if (matched) return { src: matched, name: title }
  const fallback = AGENT_ALIASES[AgentId.PLANT_SUPPORT]
  return { src: getAgentAvatarImage(AgentId.PLANT_SUPPORT), name: fallback?.displayName ?? title }
}

/**
 * Workflow node card (Figma "8. Workflow New"): header = agent avatar + title
 * (tinted while thinking/success/failed), body = two-line description +
 * optional "Human in the loop" checkbox, footer = "Status: Ready | Thinking...
 * | Success | Need Confirmation | Failed to Execute". Border color carries the
 * status tone; the per-step detail lives in the node-properties panel.
 */
function WorkflowStepNode({ data, selected }: NodeProps<WorkflowGraphNodeData>) {
  const { t } = useTranslation()
  const display = nodeStatusDisplay(data.status)
  const tone = NODE_TONE_CLASSES[display.tone]
  const avatar = nodeAvatar(data.title)
  if (data.kind === 'version' && data.versionMark) return <VersionMarker mark={data.versionMark} />

  return (
    <div className="relative">
      {/* Run Workflow pill, attached to the head of the FIRST node (Figma
          "3. Create Workflow") — anchored in flow coordinates so it pans and
          zooms with the graph instead of floating over the canvas. While the
          run is in flight (data.running) the pill becomes an actionable
          "Stop Workflow" (Figma "11. Stop", node 14757:28852) when the run is
          cancellable (data.onStop); before the run row exists it shows the
          passive "Running…" spinner instead of vanishing (Putra 2026-07-15). */}
      {(data.onRun || data.running) && (
        <div className="absolute -top-12 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center">
          {data.running && data.onStop ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                data.onStop?.()
              }}
              disabled={data.stopping}
              // Same solid dark treatment as the canvas pill. Putra, Slack 2026-09-10,
              // asked which way the variant should go and answered it himself: "for now
              // we could make them bg-foreground on both stoping the workflow" -- dark,
              // not orange. The two are on screen together during a run, so an orange
              // one beside a black one read as two different actions, which is the
              // complaint UI-85 was raised for.
              //
              // The orange is a PLACEHOLDER, not an accent ("we will change orange to
              // brand color probably", same message) -- so do not reintroduce it here
              // when the brand colour lands; this control is deliberately not orange.
              //
              // `bg-foreground`, not `bg-black`: it has to invert in dark mode, where
              // the canvas pill is already light-on-dark.
              className="nodrag nopan inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-foreground px-3.5 py-2 text-sm font-medium text-background shadow-md transition-colors hover:bg-foreground/90 disabled:cursor-default disabled:opacity-80"
            >
              {data.stopping ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  {t('workflow:builder.stopping', { defaultValue: 'Stopping…' })}
                </>
              ) : (
                <>
                  <CircleStop className="size-3.5" />
                  {t('workflow:builder.stop', { defaultValue: 'Stop Workflow' })}
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                data.onRun?.()
              }}
              disabled={data.running}
              // Same solid dark treatment as the canvas pill. Putra, Slack 2026-09-10,
              // asked which way the variant should go and answered it himself: "for now
              // we could make them bg-foreground on both stoping the workflow" -- dark,
              // not orange. The two are on screen together during a run, so an orange
              // one beside a black one read as two different actions, which is the
              // complaint UI-85 was raised for.
              //
              // The orange is a PLACEHOLDER, not an accent ("we will change orange to
              // brand color probably", same message) -- so do not reintroduce it here
              // when the brand colour lands; this control is deliberately not orange.
              //
              // `bg-foreground`, not `bg-black`: it has to invert in dark mode, where
              // the canvas pill is already light-on-dark.
              className="nodrag nopan inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-foreground px-3.5 py-2 text-sm font-medium text-background shadow-md transition-colors hover:bg-foreground/90 disabled:cursor-default disabled:opacity-80"
            >
              {data.running ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  {t('workflow:builder.running', { defaultValue: 'Running…' })}
                </>
              ) : (
                <>
                  <Play className="size-3.5 fill-current" />
                  {data.runLabel ?? t('workflow:builder.run', { defaultValue: 'Run Workflow' })}
                </>
              )}
            </button>
          )}
          <span className="h-3 w-px bg-border" />
        </div>
      )}

      <div
        className={cn(
          'flex w-[272px] flex-col overflow-hidden rounded-lg border bg-background shadow-sm transition-all duration-150 dark:bg-dark-card',
          tone.border,
          data.replacedIn && 'opacity-60',
          selected && 'ring-2 ring-cyan-400/60',
          data.clickable && 'cursor-pointer hover:shadow-md'
        )}
      >
        <EdgeHandles />

        {/* Loading bar on top of the card while this node is running
            (Putra 2026-07-15). */}
        {data.status === 'running' && (
          <div className="h-1 w-full shrink-0 animate-pulse bg-orange-500" aria-hidden />
        )}

        {/* Header: agent avatar + step title (tinted by live status) */}
        <div className={cn('flex items-center gap-2 border-b border-border/70 px-3 py-2.5', tone.header)}>
          <IntuiAvatar
            src={avatar.src}
            alt={avatar.name}
            fallback={data.title.slice(0, 2).toUpperCase()}
            size="xs"
          />
          <span className="line-clamp-2 min-w-0 flex-1 text-sm font-semibold leading-snug text-foreground">
            {data.title}
          </span>
          {data.newIn && (
            <span className="shrink-0 rounded-full bg-cyan-100 px-2 py-0.5 text-[11px] font-medium text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-300">
              {t('workflow:builder.new_in_version', { defaultValue: 'New in v{{version}}', version: data.newIn })}
            </span>
          )}
        </div>

        {/* Body: two-line description + optional HITL checkbox */}
        {(data.subtitle || data.hitl) && (
          <div className="flex flex-col gap-2 px-3 py-2.5">
            {data.subtitle && (
              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground">
                {data.subtitle}
              </p>
            )}
            {data.hitl && (
              // DISPLAY-ONLY (Putra "10. Update": "Cannot uncheck Human in the
              // loop in card") — the row shows iff HITL is effectively on for
              // this step; the toggle lives in the node-settings panel. A
              // <span> (not <label>) so clicking the text can't reach the box.
              <span className="nodrag flex items-center gap-2 text-xs font-medium text-foreground">
                {/* aria-disabled (not aria-readonly): readonly implies the
                    control is still operable/focusable — this one is not. */}
                <Checkbox
                  checked
                  tabIndex={-1}
                  aria-disabled="true"
                  className="pointer-events-none"
                />
                {t('workflow:builder.hitl', { defaultValue: 'Human in the loop' })}
              </span>
            )}
          </div>
        )}

        {/* Footer: status readout — ON A RUN ONLY.

            Jenna, feedback doc item 3: "Status: Ready begs the question — will status ever not be
            ready, and is this message worth the real estate on each step?" On the BUILDER canvas
            it never is: nothing has run, so every card says Ready, which is the same as saying
            nothing at all on eight cards in a row.

            Inside a run it earns the space — there, "Ready" means *this step has not been reached
            yet*, which is a real distinction from Success, In Progress and Need Confirmation, and
            it is what the Figma card shows. Outline emits `draft`; Detail emits the node's kind
            (`agentic`, `verify`, …). Both are pre-run, both would otherwise stamp Ready, so the
            row is shown only for a live engine status. */}
        {LIVE_RUN_STATUSES.has(data.status ?? '') && (
          <div className="border-t border-border/70 bg-muted/40 px-3 py-2 dark:bg-dark-muted/40">
            <span className={cn('text-xs font-medium', tone.footerText)}>
              {t('workflow:builder.status_prefix', { defaultValue: 'Status:' })}{' '}
              {t(display.labelKey, { defaultValue: display.label, version: data.replacedIn })}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default memo(WorkflowStepNode)
